import { inject, Injectable } from '@angular/core';
import { DatiqGraphqlApolloService } from '@dat/datiq-graphql';

import { gql } from 'apollo-angular';
import { get } from 'lodash';
import { firstValueFrom } from 'rxjs';

type LocationCode = {
    code: string;
    type: string;
    name: string;
};

export type LocationRes = {
    id: string;
    city: string;
    county: string;
    displayName: string;
    latitude: number;
    longitude: number;
    placeId: number;
    postalCode: string;
    stateProv: string;
    geoCodes: LocationCode[];
    error: string;
    hasError: boolean;
};


@Injectable({
  providedIn: 'root'
})
export class IqRateLaneGeoService {

    private readonly _apollo: DatiqGraphqlApolloService = inject(DatiqGraphqlApolloService);

    constructor() {}

    private locationCodeMap: Map<string, LocationRes> = new Map();
    private directionsMap: Map<string, google.maps.DirectionsResult> = new Map();

    async getDirections(origin: Promise<any>, destination: Promise<any>): Promise<any> {

        return Promise.all([
            origin,
            destination
        ]).then(async ([ originInfo, destinationInfo ]) => {
            if (!originInfo?.pointId || !destinationInfo?.pointId) {
                return null;
            }

            if (this.directionsMap.has(`${originInfo.pointId}-${destinationInfo.pointId}`)) {
                return this.directionsMap.get(`${originInfo.pointId}-${destinationInfo.pointId}`);
            }

            const directions = new google.maps.DirectionsService();
            const route = await directions.route({
                origin: { lat: originInfo.latitude, lng: originInfo.longitude },
                destination: { lat: destinationInfo.latitude, lng: destinationInfo.longitude },
                travelMode: google.maps.TravelMode.DRIVING,
                provideRouteAlternatives: false
            });

            this.directionsMap.set(`${originInfo.pointId}-${destinationInfo.pointId}`, route);
            return route;
        }).catch(() => {
            return null;
        });
    }

    async getLocation(search: string): Promise<any> {
        search = search.toLowerCase();
        if (this.locationCodeMap.has(search)) {
            const location =  this.locationCodeMap.get(search);
            return location;
        }

        const queryResult = this._apollo.getApollo().query<{ suggestions: any[] }>({
            query: getLocationQuery(),
            variables: {
                query: {
                    search
                }
            }
        });
        try {
            const results = await firstValueFrom(queryResult);
            if (get(results, 'data.response.hasError')) {
                return null;
            }

            const location =  get(results, 'data.response', {});
            this.locationCodeMap.set(search, location);
            return location;
        } catch (err) {
            return null;
        }
    }
}

function getLocationQuery() {
    return gql`
        query ($query: PocGeoLocationQuery!) {
            response: PocGeoLocation(query: $query) {
                city
                stateProv
                postalCode
                pointId: placeId
                latitude
                longitude
                geoCodes {
                    code
                    type
                    name
                }
                hasError
                error
            }
        }
    `;
}